import React, { useState } from 'react';
import {
  faArrowDownToLine,
  faChevronDown,
  faChevronRight,
  faXmark,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Text, ThemeIcon, Tooltip, formatFileSize } from '@cowbell/ui';
import type { MessageInfoAttachment } from '../../../api/cmail/cmail.dto';
import {
  attachmentPreviews,
  useAttachmentDownload,
  useAttachmentOpen,
} from '../hooks/useAttachmentOpen';
import { isAudioAttachment } from '../utils/audioAttachmentUtils';
import {
  attachmentFileMeta,
  attachmentMetaLabel,
} from '../utils/attachmentFileType';
import { AudioAttachmentChipContainer } from './AudioAttachmentChipContainer';
import classes from './AttachmentChip.module.css';

interface AttachmentChipProps {
  readonly attachment: MessageInfoAttachment;
  /** Composer only: replaces the download action with a remove action. */
  readonly onRemove?: () => void;
  /** Invoice PDFs split out of this attachment, shown in an expandable list. */
  readonly slices?: readonly MessageInfoAttachment[];
}

interface SliceToggle {
  readonly count: number;
  readonly expanded: boolean;
  readonly onToggle: () => void;
}

const FileAttachmentChip = ({
  attachment,
  onRemove,
  sliceToggle,
}: Omit<AttachmentChipProps, 'slices'> & {
  readonly sliceToggle?: SliceToggle;
}) => {
  const onOpen = useAttachmentOpen(attachment);
  const onDownload = useAttachmentDownload(attachment);
  const fileType = attachmentFileMeta(attachment);
  const sizeLabel = formatFileSize(Number(attachment.docSize) || 0);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onOpen();
    }
  };

  // The pill is not a control: opening and downloading are sibling buttons,
  // so the download button is never nested inside another button.
  return (
    <Box className={`${classes.attachmentChip} ${classes.fileChip}`}>
      <Tooltip label={attachment.docName} openDelay={500}>
        <Box
          role="button"
          tabIndex={0}
          className={classes.fileOpen}
          onClick={onOpen}
          onKeyDown={handleKeyDown}
          aria-label={`Download or preview ${attachment.docName}`}
        >
          <ThemeIcon
            aria-hidden
            variant="light"
            color={fileType.color}
            className={classes.typeTile}
          >
            <FontAwesomeIcon
              icon={fileType.icon}
              className={classes.typeIcon}
            />
          </ThemeIcon>
          <Box className={classes.fileText}>
            <Text className={classes.fileName}>{attachment.docName}</Text>
            <Text className={classes.fileMeta} c="dimmed">
              {attachmentMetaLabel(
                fileType.typeName,
                sizeLabel,
                attachment.pageRange
              )}
            </Text>
          </Box>
        </Box>
      </Tooltip>
      {sliceToggle && (
        <button
          type="button"
          className={classes.sliceToggle}
          onClick={sliceToggle.onToggle}
          aria-expanded={sliceToggle.expanded}
          aria-label={`${sliceToggle.expanded ? 'Hide' : 'Show'} ${
            sliceToggle.count
          } invoice PDF${sliceToggle.count === 1 ? '' : 's'}`}
        >
          <FontAwesomeIcon
            icon={sliceToggle.expanded ? faChevronDown : faChevronRight}
          />
          <span aria-hidden>{sliceToggle.count}</span>
        </button>
      )}
      {onRemove && (
        <button
          type="button"
          className={classes.chipAction}
          onClick={onRemove}
          aria-label={`Remove ${attachment.docName}`}
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
      )}
      {/* A click already downloads anything that does not preview. */}
      {!onRemove && attachmentPreviews(attachment) && (
        <button
          type="button"
          className={classes.chipAction}
          onClick={onDownload}
          aria-label={`Download ${attachment.docName}`}
        >
          <FontAwesomeIcon icon={faArrowDownToLine} />
        </button>
      )}
    </Box>
  );
};

// A removable (composer) attachment is one you just attached, so it gets the
// plain file chip rather than the player.
export const AttachmentChip = ({
  attachment,
  onRemove,
  slices,
}: AttachmentChipProps) => {
  const [expanded, setExpanded] = useState(false);

  if (isAudioAttachment(attachment) && !onRemove) {
    return <AudioAttachmentChipContainer attachment={attachment} />;
  }
  if (!slices || slices.length === 0) {
    return <FileAttachmentChip attachment={attachment} onRemove={onRemove} />;
  }

  return (
    <Box className={classes.chipWithSlices}>
      <FileAttachmentChip
        attachment={attachment}
        onRemove={onRemove}
        sliceToggle={{
          count: slices.length,
          expanded,
          onToggle: () => setExpanded((open) => !open),
        }}
      />
      {expanded && (
        <Box className={classes.sliceList}>
          {slices.map((slice) => (
            <AttachmentChip key={slice.docId} attachment={slice} />
          ))}
        </Box>
      )}
    </Box>
  );
};
